import React, {lazy, Suspense, useEffect} from 'react';
import {BrowserRouter as Router, Redirect, Route, Switch, useHistory} from 'react-router-dom';
import {ConnectedRouter} from 'connected-react-router';
import {useDispatch, useSelector} from 'react-redux';
import {ToastContainer} from 'react-toastify';

import 'react-toastify/dist/ReactToastify.css';
import './App.scss';

import './localization';

import {routes} from './RoutesConst';

import {getTenant, getTenantQuestionnairesFromServer, setSlug} from './Redux/actions/tenant';
import {prefetchAllProviders} from './Redux/actions/providers';
import {preloadProviderPhotos} from './containers/doctorProvider/providerPhoto';
import {changeOrder} from './Redux/actions/order';
import {logOut} from "./Redux/actions/auth";

import {getIsAuth, getIsAuthInStaging} from './Redux/selectors/auth';
import {getErrorOrSuccessScreen} from "./Redux/selectors/modal";
import {getSlug} from './Redux/selectors/tenant';

import ScrollToTop from './Helpers/scrollToTop';
import {MainModal, ModalCancelAppointment, ModalChangingInsurance, ModalForWebCamera} from './components/modals'
import Loading from './components/helpers/loading';
import ContentWrapper from './components/wrappers/contentWrapper';
import ErrorOrSuccessPage from "./containers/errorOrSuccessPage";
import AuthStagingPage from "./containers/authStagingPage";

import {useInactivityTime, useMount, usePrevious} from "./hooks";

// Lazy components are created once at module scope: creating them inline in render
// gives react-router a new component identity on every App re-render, which
// unmounts the current page and re-triggers the Suspense fallback without navigation.
const HomePage = lazy(() => import('./containers/notAuth/home'));
const IntroducePage = lazy(() => import('./containers/introducePage'));
const RecoveryPasswordPage = lazy(() => import('./containers/recoveryPasswordPage'));
const VerifyEmailPage = lazy(() => import('./containers/verifyEmailPage'));
const ConfirmationPage = lazy(() => import('./containers/auth/confirmation'));
const SignInPage = lazy(() => import('./containers/auth/signIn'));
const LoginPage = lazy(() => import('./containers/notAuth/login'));
const SignUpPage = lazy(() => import('./containers/notAuth/signup'));
const VerifyCodePage = lazy(() => import('./containers/notAuth/verifyCode'));
const CoincidencePage = lazy(() => import('./containers/coincidencePage'));
const AppointmentPage = lazy(() => import('./containers/auth/appointment'));
const InsuranceProviderPage = lazy(() => import('./containers/insuranceProvider'));
const DoctorProviderPage = lazy(() => import('./containers/doctorProvider'));
const ChooseAppointmentTypePage = lazy(() => import('./containers/chooseAppointmentTypePage'));
const ChooseSpecificTypePage = lazy(() => import('./containers/chooseSpecificTypePage'));
const AdditionalSpecificTypePage = lazy(() => import('./containers/additionalSpecificTypePage'));
const SupportPage = lazy(() => import('./containers/support'));
const AppointmentsPage = lazy(() => import('./containers/appointmentsPage'));
const EditAppointmentPage = lazy(() => import('./containers/editAppointmentPage'));
const EditPrescriptionPage = lazy(() => import('./containers/editPrescriptionPage'));
const SummaryPage = lazy(() => import('./containers/auth/summary'));
const ProfilePage = lazy(() => import('./containers/auth/profile'));
const PaymentPage = lazy(() => import('./containers/paymentPage'));
const PaymentCardsPage = lazy(() => import('./containers/paymentCardsPage'));
const AddNewCreditCardPage = lazy(() => import('./containers/addNewCreditCard'));
const InsuranceFlowPage = lazy(() => import('./containers/insuranceFlow'));

const App = ({history}) => {
    const historyRoute = useHistory();
    const dispatch = useDispatch();
    const slug = useSelector(getSlug);
    const isAuth = useSelector(getIsAuth);
    const errorOrSuccessScreen = useSelector(getErrorOrSuccessScreen)
    const prevSlug = usePrevious(slug);
    const isAuthInStaging = useSelector(getIsAuthInStaging);

    useInactivityTime(isAuth ? () => {
        dispatch(logOut(history));
        dispatch(changeOrder({appointment: {}, doctor: {}, paymentStatus: { status: '' }}));
        historyRoute.push(`/${slug}/`);
    } : () => {});

    useMount(() => dispatch(setSlug(window.location.pathname.split("/")[1])))

    useEffect(() => {
        if (slug) {
            dispatch(getTenant(slug))
            // Warm the first booking steps while the patient is still on the landing page.
            dispatch(getTenantQuestionnairesFromServer())
            dispatch(prefetchAllProviders()).then((res) => preloadProviderPhotos(res?.payload?.providers))
        }
        if (prevSlug && prevSlug !== slug) {
            dispatch(changeOrder({appointment: {}, doctor: {}}));
        }
    }, [slug]);

    return (
        <ConnectedRouter history={history}>
            <Router>
                <div className="App">
                    <Suspense fallback={<Loading/>}>
                        {process.env.NODE_ENV === 'development' && !isAuthInStaging && (
                            <AuthStagingPage />
                        )}
                        <ModalForWebCamera/>
                        <MainModal/>
                        <ModalChangingInsurance/>
                        <ModalCancelAppointment/>
                        <ScrollToTop>
                            {!isAuth ? (
                                <Switch>
                                    {errorOrSuccessScreen.isOpen && (<ContentWrapper><ErrorOrSuccessPage/></ContentWrapper>)}
                                    <Route path={routes.home} exact
                                           component={HomePage}/>
                                    <ContentWrapper
                                        container
                                        component="main"
                                    >
                                        <Switch>
                                            <Route path={routes.tenants} exact
                                                   component={IntroducePage} />
                                            <Route path={routes.recoveryPassword} exact
                                                   component={RecoveryPasswordPage} />
                                            <Route path={routes.verifyEmail} exact
                                                   component={VerifyEmailPage} />
                                            <Route path={routes.confirmation} exact
                                                   component={ConfirmationPage}/>
                                            <Route path={routes.signin} exact
                                                   component={SignInPage}/>
                                            <Route path={routes.login} exact
                                                   component={LoginPage}/>
                                            <Route path={routes.signup} exact
                                                   component={SignUpPage}/>
                                            <Route path={routes.verify} exact
                                                   component={VerifyCodePage} />
                                            <Route path={routes.coincidencePage} exact
                                                   component={CoincidencePage} />
                                            <Route path={routes.appointment} exact
                                                   component={AppointmentPage}/>
                                            <Route path={routes.insuranceProvider} exact
                                                   component={InsuranceProviderPage}/>
                                            <Route path={routes.doctorProvider} exact
                                                   component={DoctorProviderPage}/>
                                            <Route path={routes.chooseAppointment} exact
                                                   component={ChooseAppointmentTypePage}/>
                                            <Route path={routes.chooseSpecificType} exact
                                                   component={ChooseSpecificTypePage}/>
                                            <Route path={routes.additionalSpecificType} exact
                                                   component={AdditionalSpecificTypePage}/>
                                            <Route path={routes.support.home}
                                                   component={SupportPage} />
                                            <Route path='*'>
                                                <Redirect to={`/${slug}/login`}/>
                                            </Route>
                                        </Switch>
                                    </ContentWrapper>
                                </Switch>
                            ) : (
                                <ContentWrapper
                                    container
                                    component="main"
                                >
                                    <Switch>
                                        {errorOrSuccessScreen.isOpen && (<ErrorOrSuccessPage/>)}
                                        <Route path={routes.tenants} exact
                                               component={IntroducePage} />
                                        <Route path={routes.userAppointments} exact
                                               component={AppointmentsPage} />
                                        <Route path={routes.userAppointment} exact
                                               component={EditAppointmentPage} />
                                        <Route path={routes.userPrescription} exact
                                               component={EditPrescriptionPage} />
                                        <Route path={routes.confirmation} exact
                                               component={ConfirmationPage}/>
                                        <Route path={routes.summary} exact
                                               component={SummaryPage}/>
                                        <Route path={routes.chooseAppointment} exact
                                               component={ChooseAppointmentTypePage}/>
                                        <Route path={routes.chooseSpecificType} exact
                                               component={ChooseSpecificTypePage}/>
                                        <Route path={routes.additionalSpecificType} exact
                                               component={AdditionalSpecificTypePage}/>
                                        <Route path={routes.appointment} exact
                                               component={AppointmentPage}/>
                                        <Route path={routes.insuranceProvider} exact
                                               component={InsuranceProviderPage}/>
                                        <Route path={routes.profile.home}
                                               component={ProfilePage}/>
                                        <Route path={routes.doctorProvider} exact
                                               component={DoctorProviderPage}/>
                                        <Route path={routes.paymentPage} exact
                                               component={PaymentPage} />
                                        <Route path={routes.paymentCards} exact
                                               component={PaymentCardsPage} />
                                        <Route path={routes.payAddCard} exact
                                               component={AddNewCreditCardPage} />
                                        <Route path={routes.verifyEmail} exact
                                               component={VerifyEmailPage} />
                                        <Route path={routes.recoveryPassword} exact
                                               component={RecoveryPasswordPage} />
                                        <Route path={routes.insuranceFlow.home}
                                               component={InsuranceFlowPage} />
                                        <Route path={routes.support.home}
                                               component={SupportPage} />
                                        <Route path='*'>
                                            <Redirect to={slug ? `/${slug}/choose-appointment-type` : `/`}/>
                                        </Route>
                                    </Switch>
                                </ContentWrapper>
                            )}
                        </ScrollToTop>
                        <ToastContainer autoClose={5000}/>
                    </Suspense>
                </div>
            </Router>
        </ConnectedRouter>
    );
};

export default App;
